import * as React from 'react'
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table'
import { DataTablePagination } from 'features/tasks/components/data-table-pagination'
import { cn } from 'lib/utils'

interface DataTableProps<TData> {
  // TanStack's recommended typing: columns mix accessors of different value types
  columns: ColumnDef<TData, any>[]
  data: TData[]
  pageCount: number
  pageIndex: number
  pageSize: number
  onPageIndexChange: (pageIndex: number) => void
  toolbar?: React.ReactNode
  /**
   * Renders a row as a card on phones (below `md`), instead of a table too
   * wide to read there. Without it the table is shown at every width.
   */
  renderMobileRow?: (row: TData) => React.ReactNode
  /** Key of a mobile card; the row index if missing. */
  getRowKey?: (row: TData) => React.Key
}

export function GenericDataTable<TData>({
  columns,
  data,
  pageCount,
  pageIndex,
  pageSize,
  onPageIndexChange,
  toolbar,
  renderMobileRow,
  getRowKey,
}: DataTableProps<TData>) {
  const [rowSelection, setRowSelection] = React.useState({})
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({})
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  )
  const [sorting, setSorting] = React.useState<SortingState>([])

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    manualPagination: true, // Enable manual server-side pagination
  })

  return (
    <div className='space-y-4'>
      {toolbar}
      {renderMobileRow && (
        <div className='space-y-3 md:hidden'>
          {data.length ? (
            data.map((row, index) => (
              <React.Fragment key={getRowKey?.(row) ?? index}>
                {renderMobileRow(row)}
              </React.Fragment>
            ))
          ) : (
            <p className='py-8 text-center text-muted-foreground'>
              Nessun risultato.
            </p>
          )}
        </div>
      )}
      <div
        className={cn(
          'rounded-md border',
          renderMobileRow && 'hidden md:block'
        )}
      >
        <Table>
          <TableHeader>
            {table?.getHeaderGroups()?.map((headerGroup, index) => {
              return (
                <TableRow key={index}>
                  {headerGroup.headers.map((header, i) => {
                    return (
                      <TableHead key={'header_' + i} colSpan={header.colSpan}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    )
                  })}
                </TableRow>
              )
            })}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className={cn({
                    'bg-violet-50 dark:bg-muted':
                      (row.original as { status?: string })?.status ===
                        'pending' ||
                      (row.original as { consumed?: boolean })?.consumed ===
                        false ||
                      (row.original as { activated?: boolean })?.activated ===
                        false,
                    'bg-red-100 dark:bg-red-500/20':
                      (row.original as { status?: string })?.status ===
                      'failed',
                    'bg-yellow-50 dark:bg-yellow-500/15':
                      (row.original as { status?: string })?.status ===
                      'running',
                  })}
                >
                  {row.getVisibleCells().map((cell, i) => (
                    <TableCell key={'cell_' + i}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className='h-24 text-center'
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <DataTablePagination
        pageCount={pageCount}
        pageIndex={pageIndex}
        onPageIndexChange={onPageIndexChange}
      />
    </div>
  )
}
